import React, { createContext, use, useMemo, ReactNode } from "react";
import { getProductFromSubdomain, PRODUCT_TYPES } from "utils/productDetection";
import { getProductConfig } from "config/productConfig";
import { useFeatureFlagContext } from "./FeatureFlagContext";

/**
 * Product context value interface
 */
export interface ProductContextValue {
    productType: string;
    config: Record<string, any>;
    isDTO: boolean;
    isMLTA: boolean;
    isGIWL: boolean;
}

/**
 * ProductContext
 * Provides product type and configuration throughout the application
 */
export const ProductContext = createContext<ProductContextValue | null>(null);

interface ProductProviderProps {
    children: ReactNode;
}

/**
 * ProductProvider
 * Wraps the application and provides product context to all children
 * Must be wrapped by FeatureFlagProvider to access feature flags
 */
export const ProductProvider = ({ children }: ProductProviderProps) => {
    const { featureFlags } = useFeatureFlagContext();
    const productType = useMemo(() => getProductFromSubdomain(), []);
    const productConfig = useMemo(() => getProductConfig(productType, featureFlags || []), [productType, featureFlags]);

    const value = useMemo(
        () => ({
            productType,
            config: productConfig,
            isDTO: productType === PRODUCT_TYPES.DTO,
            isMLTA: productType === PRODUCT_TYPES.MLTA,
            isGIWL: productType === PRODUCT_TYPES.GIWL,
        }),
        [productType, productConfig]
    );

    return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

/**
 * useProductContext hook
 * Access product type and configuration from any component
 * @returns {ProductContextValue} { productType, config, isDTO, isMLTA, isGIWL }
 */
export const useProductContext = (): ProductContextValue => {
    const context = use(ProductContext);

    if (!context) {
        throw new Error("useProductContext must be used within a ProductProvider");
    }

    return context;
};

/**
 * useProductType hook
 * Shorthand to get just the product type
 * @returns {string} Product type ('dto', 'mlta', or 'giwl')
 */
export const useProductType = (): string => {
    const { productType } = useProductContext();
    return productType;
};

/**
 * useProductConfig hook
 * Shorthand to get just the product configuration
 * @returns {object} Product configuration
 */
export const useProductConfig = (): Record<string, any> => {
    const { config } = useProductContext();
    return config;
};
